import { z } from "zod";
import { TOTAL_PLANET_IMAGE_COUNT } from "../constants/planets.js";
import { MIN_TRADE_AMOUNT } from "../constants/trade.js";
import { TRADEABLE_RESOURCES } from "../types/trade.types.js";
/**
 * Schema for galaxy position validation
 */
export const GalaxyPositionSchema = z.object({
    galaxy: z.number().min(1).max(999),
    system: z.number().min(1).max(999),
    position: z.number().min(1).max(24),
});
/**
 * Schema for creating a new planet
 */
export const CreatePlanetSchema = z.object({
    name: z.string().min(3).max(50),
    coordinates: GalaxyPositionSchema,
    planetType: z.string(),
});
/**
 * Schema for upgrading a building
 */
export const UpgradeBuildingSchema = z.object({
    planetId: z.string(),
    buildingType: z.enum([
        "metalMine",
        "mineralRefinery",
        "gasPlant",
        "solarPlant",
        "fusionPlant",
        "metalStorage",
        "mineralStorage",
        "gasStorage",
        "laboratory",
        "shipyard",
        "tradeCenter",
        "robotFactory",
        "automationCenter",
        "techCenter",
        "hangars",
        "government",
        "recyclingPlant",
    ]),
    /**
     * The plot to build on (DS-28) — only meaningful for a level 0→1
     * construct; the `upgradeBuilding` handler ignores it for a level ≥ 1
     * upgrade, which already has a plot. Optional so `destroyBuilding` and
     * every existing call site (both reuse this schema) keep working
     * unchanged.
     */
    plotId: z.string().optional(),
});
/**
 * Schema for renaming a planet and/or assigning a preset image. At least one
 * of `name`/`planetType` must be present. The schema accepts any valid image
 * id (1..`TOTAL_PLANET_IMAGE_COUNT`); the premium-image entitlement gate
 * (ids above `FREE_PLANET_IMAGE_COUNT` require an unlock) is enforced in the
 * `updatePlanet` handler, which knows the player's `unlockedPlanetImages`.
 */
export const UpdatePlanetSchema = z.object({
    planetId: z.string(),
    name: z.string().trim().min(3).max(50).optional(),
    planetType: z.number().int().min(1).max(TOTAL_PLANET_IMAGE_COUNT).optional(),
}).refine((data) => data.name !== undefined || data.planetType !== undefined, {
    message: "At least one of name or planetType must be provided",
});
/**
 * Schema for permanently deleting a non-main planet (issue #28).
 */
export const DeletePlanetSchema = z.object({
    planetId: z.string(),
});
/**
 * Schema for trading one economic resource for another at the Trade
 * Center. `from`/`to` are restricted to the three tradeable resources
 * (never energy/ozone/scrap — see `TradeableResource`); `amount` is the
 * quantity of `from` given up, not the amount received, and must be at
 * least `MIN_TRADE_AMOUNT`.
 */
export const TradeResourcesSchema = z.object({
    planetId: z.string(),
    from: z.enum([...TRADEABLE_RESOURCES]),
    to: z.enum([...TRADEABLE_RESOURCES]),
    amount: z.number().int().min(MIN_TRADE_AMOUNT),
}).refine((data) => data.from !== data.to, {
    message: "from and to must be different resources",
});
//# sourceMappingURL=planet.schemas.js.map